import { BACKEND_KEYS, STORAGE_KEYS } from '../../common/consts/app-keys.const';
import {
  HttpFactoryService,
  EnhancedWithAuthHttpService
} from '../../common/services';
import { IProduct } from '../types';

class HomeService {
  httpService: EnhancedWithAuthHttpService;

  constructor(private httpFactory: HttpFactoryService) {
    this.httpService = httpFactory.createAuthHttpService();
  }

  async getCheckoutSession(
    duration: '1m' | '1y' | 'free' | 'perpetual',
    product: string | undefined,
    // Auto-renewal consent metadata. The backend requires it for every paid
    // checkout and persists it as proof of consent (ROSCA / state
    // auto-renewal statutes) — callers must only pass it after the buyer has
    // actually ticked the consent checkbox.
    arConsent: {
      version: string;
      locale: string;
      price: string;
      // Required (and only sent) for Unreal Engine products: buyer affirmed
      // they hold a valid UE license for the matching engine version.
      ueLicensee?: boolean;
    },
    // Seats. Omitted entirely when the caller has no seat picker, so every
    // pre-existing checkout sends exactly the querystring it always did and
    // the backend defaults it to one.
    quantity?: number,
    // Seat top-up: add the seats to this existing licence instead of issuing a
    // new one. One customer, one key.
    topUpLicenseId?: number
  ) {
    // A one-time (perpetual) purchase does not auto-renew, so we send NO auto-renewal
    // consent — only the UE-licensee affirmation gates it. Recurring plans still send
    // the AR consent the backend requires and records.
    const isOneTime = duration === 'perpetual';
    const result: string | void = await this.httpService.get(
      BACKEND_KEYS.CHECKOUT_SESSION,
      {
        params: {
          duration,
          ...(product ? { product } : {}),
          ...(isOneTime
            ? {}
            : {
                arConsent: 'true',
                arConsentVersion: arConsent.version,
                arLocale: arConsent.locale,
                arPrice: arConsent.price
              }),
          ...(arConsent.ueLicensee ? { ueLicensee: 'true' } : {}),
          ...(quantity && quantity > 1 ? { quantity: String(quantity) } : {}),
          ...(topUpLicenseId
            ? { topUpLicenseId: String(topUpLicenseId) }
            : {})
        }
      }
    );
    if (!result) throw new Error('Error while getting checkout session');

    return result;
  }

  async getProduct() {
    const result: IProduct | void = await this.httpService.get(BACKEND_KEYS.PRODUCT);
    if (!result) throw new Error('Error while getting product');
    return result;
  }

  async getFreeKey() {
    const result: string | void = await this.httpService.get(
      BACKEND_KEYS.GET_FREE_KEY
    );
    if (!result) throw new Error('Error while getting free key');

    return result;
  }

  // Claim the free Subdivs tier for the signed-in account (idempotent server-side).
  // `eulaVersion` is the Subdivs EULA the user accepted at claim (clickwrap); the backend
  // requires it and records it as terms-acceptance evidence.
  async claimSubdivsFree(eulaVersion: string) {
    return this.httpService.get(BACKEND_KEYS.CLAIM_SUBDIVS_FREE, {
      params: { eula: eulaVersion }
    });
  }

  async getPlanPrice(
    duration: '1m' | '1y' | 'free' | 'perpetual',
    product?: string
  ) {
    if (duration === 'free') return 0;
    const result: number | void = await this.httpService.get(
      BACKEND_KEYS.GET_PLAN_PRICE,
      { params: { duration, ...(product ? { product } : {}) } }
    );
    if (!result) throw new Error('Error while getting plan price');

    const price = (result / 100).toFixed(2);
    return price;
  }
}

const homeService = new HomeService(new HttpFactoryService());

export { homeService };
